import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Search, AlertTriangle, CheckCircle2, RefreshCw, ArrowRight, ChevronsUpDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function ScoreBadge({ score }) {
  let cls = 'bg-gray-100 text-gray-700';
  let label = 'no match';
  if (score >= 90) { cls = 'bg-emerald-100 text-emerald-800'; label = 'match fuerte'; }
  else if (score >= 60) { cls = 'bg-emerald-50 text-emerald-700'; label = 'match'; }
  else if (score >= 30) { cls = 'bg-amber-100 text-amber-800'; label = 'parcial'; }
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`} data-testid="bank-search-score-badge">
      {score} · {label}
    </span>
  );
}

export default function BankSearchModal({ orderCode, onClose, onReconciled, setToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [daysWindow, setDaysWindow] = useState(1);
  const [reconciling, setReconciling] = useState(null);

  const load = async (window) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${BACKEND_URL}/api/super-admin/orders/${orderCode}/bank-search`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { days_window: window, only_credits: true },
        }
      );
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  // initial load
  useEffect(() => { load(daysWindow); }, []);

  const handleApply = (window) => {
    setDaysWindow(window);
    load(window);
  };

  const handleReconcile = async (mov) => {
    if (!window.confirm(`¿Marcar la orden ${orderCode} como pagada con esta referencia bancaria?\n\nMonto: ${mov.monto}\nReferencia: ${mov.referencia}`)) return;
    setReconciling(mov.referencia);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/super-admin/orders/${orderCode}/reconcile`,
        {
          payment_method: 'p2p',
          payment_reference: mov.referencia,
          amount: mov.monto,
          notes: `Conciliado vía bank-search. Match score: ${mov.match_score}, reasons: ${(mov.match_reasons || []).join(', ')}`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setToast?.({ type: 'success', message: `Orden ${orderCode} reconciliada` });
      onReconciled?.();
      onClose();
    } catch (e) {
      setToast?.({ type: 'error', message: e.response?.data?.detail || e.message });
    } finally {
      setReconciling(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="bank-search-modal">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ backgroundColor: '#051C2C' }}>
          <div className="flex items-center gap-3 text-white">
            <Search className="w-5 h-5" />
            <div>
              <h3 className="text-lg font-bold">Buscar movimientos en Banco</h3>
              <p className="text-xs text-blue-200 font-mono">Orden {orderCode}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-300" data-testid="bank-search-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {/* Order summary */}
          {data?.order && (
            <div className="rounded-xl border bg-slate-50 p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-semibold">Monto esperado</div>
                  <div className="text-base font-bold text-slate-900">{data.order.currency} {Number(data.order.amount).toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-semibold">Estado</div>
                  <div className="text-sm font-semibold capitalize">{data.order.status}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-semibold">Pagador</div>
                  <div className="text-sm">{data.order.payer_name}</div>
                  <div className="text-xs text-slate-500 font-mono">{data.order.payer_document}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-slate-500 font-semibold">Cuenta consultada</div>
                  <div className="text-xs font-mono">{data.account?.number}</div>
                  <div className="text-[10px] text-slate-500">{data.account?.titular} {data.account?.owns ? '(propia merchant)' : '(Caura)'}</div>
                </div>
              </div>
            </div>
          )}

          {/* Window controls */}
          <div className="rounded-xl border bg-white p-3 flex items-center gap-3 flex-wrap">
            <span className="text-xs uppercase text-slate-500 font-semibold tracking-wide flex items-center gap-2"><ChevronsUpDown className="w-3 h-3" /> Ventana</span>
            {[0, 1, 2, 7, 30].map(w => (
              <button
                key={w}
                onClick={() => handleApply(w)}
                data-testid={`bank-search-window-${w}`}
                className={`px-3 py-1 rounded-lg text-xs font-medium border ${daysWindow === w ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              >
                {w === 0 ? 'Sólo el día' : `±${w} día${w > 1 ? 's' : ''}`}
              </button>
            ))}
            <button onClick={() => load(daysWindow)} className="ml-auto text-xs flex items-center gap-1 text-slate-600 hover:text-slate-900" data-testid="bank-search-refresh">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refrescar
            </button>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          {data?.bank_error && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <div>
                <div className="font-semibold">Banco Plaza no respondió</div>
                <div className="text-xs">{data.bank_error}</div>
              </div>
            </div>
          )}

          {data?.best_match && (
            <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-2">
                <CheckCircle2 className="w-4 h-4" /> Mejor coincidencia detectada
              </div>
              <div className="text-sm grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><span className="text-xs text-emerald-700">Monto</span><div className="font-semibold">{data.best_match.monto}</div></div>
                <div><span className="text-xs text-emerald-700">Referencia</span><div className="font-mono">{data.best_match.referencia}</div></div>
                <div><span className="text-xs text-emerald-700">Fecha</span><div>{data.best_match.fecha}</div></div>
                <div className="text-right"><ScoreBadge score={data.best_match.match_score} /></div>
              </div>
            </div>
          )}

          {/* Movements table */}
          <div className="rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-left">Naturaleza</th>
                  <th className="px-3 py-2 text-right">Monto</th>
                  <th className="px-3 py-2 text-left">Referencia</th>
                  <th className="px-3 py-2 text-left">Descripción</th>
                  <th className="px-3 py-2 text-left">Match</th>
                  <th className="px-3 py-2 text-left">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(data?.movements || []).length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-slate-500">
                      {data?.bank_error ? 'Sin datos del banco.' : 'No se encontraron movimientos en la ventana seleccionada.'}
                    </td>
                  </tr>
                )}
                {(data?.movements || []).map((mov, idx) => (
                  <tr key={idx} className={`hover:bg-slate-50 ${mov.match_score >= 60 ? 'bg-emerald-50/40' : ''}`} data-testid={`bank-search-row-${idx}`}>
                    <td className="px-3 py-2 text-xs whitespace-nowrap">{mov.fecha} <span className="text-slate-400">{mov.hora || ''}</span></td>
                    <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${mov.naturaleza === 'CR' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700'}`}>{mov.naturaleza}</span></td>
                    <td className="px-3 py-2 text-right font-semibold font-mono">{Number(mov.monto || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{mov.referencia}</td>
                    <td className="px-3 py-2 text-xs max-w-xs truncate" title={mov.descripcion}>{mov.descripcion || '-'}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-col gap-1">
                        <ScoreBadge score={mov.match_score} />
                        {(mov.match_reasons || []).length > 0 && (
                          <span className="text-[10px] text-slate-500">{mov.match_reasons.join(', ')}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {data?.order?.status === 'pending' && (
                        <button
                          onClick={() => handleReconcile(mov)}
                          disabled={reconciling === mov.referencia}
                          data-testid={`bank-search-reconcile-${idx}`}
                          className="px-2 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {reconciling === mov.referencia ? '...' : (<>Reconciliar <ArrowRight className="w-3 h-3" /></>)}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-xs text-slate-500">
            Total: {data?.total_found ?? 0} movimientos en ventana ±{daysWindow} día(s).
            Cuenta {data?.account?.number} · Banco {data?.account?.bank_code}.
          </div>
        </div>

        <div className="px-6 py-3 border-t bg-slate-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-1.5 text-sm border rounded-lg hover:bg-white">Cerrar</button>
        </div>
      </div>
    </div>
  );
}
